
import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Play, Volume2, VolumeX } from 'lucide-react';

export interface VideoPlayerProps {
  videoSrc: string;
  posterSrc?: string | null;
  autoplay?: boolean;
  controls?: boolean;
  loop?: boolean;
  muted?: boolean;
  className?: string;
  hideControls?: boolean;
  alwaysShowControls?: boolean;
  onPlayStateChange?: (isPlaying: boolean) => void;
  onEnded?: () => void;
  onError?: () => void;
  onLoad?: () => void;
  onVisible?: () => void;
  isPlaying?: boolean;
  preload?: "auto" | "metadata" | "none";
  src?: string;
  poster?: string;
  isMobile?: boolean;
  onOpenLicenseDialog?: () => void;
  forceFirstFrame?: boolean;
  playsInline?: boolean;
  disableDownload?: boolean;
  showSoundControls?: boolean;
}

export const VideoPlayer = ({
  videoSrc,
  posterSrc,
  autoplay = false,
  controls = true,
  loop = false,
  muted = false,
  className = "",
  hideControls = false,
  alwaysShowControls = false,
  onPlayStateChange,
  onEnded,
  onError,
  onLoad,
  onVisible,
  isPlaying: controlledIsPlaying,
  preload = "auto",
  src,
  poster,
  isMobile = false,
  onOpenLicenseDialog,
  forceFirstFrame = false,
  playsInline = true,
  disableDownload = false,
  showSoundControls = false
}: VideoPlayerProps) => {
  const actualVideoSrc = videoSrc || src;
  const actualPosterSrc = posterSrc || poster;
  
  const [isPlaying, setIsPlaying] = useState(autoplay);
  const [showCustomControls, setShowCustomControls] = useState(false);
  const [videoError, setVideoError] = useState<string | null>(null);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [isMuted, setIsMuted] = useState(muted);
  const [frameLoaded, setFrameLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [key, setKey] = useState(0);
  
  // Never use poster when we want to force first frame display
  const shouldUsePoster = !forceFirstFrame && isMobile && actualPosterSrc;

  // Debug video source on mount
  useEffect(() => {
    if (actualVideoSrc) {
      console.log("VideoPlayer - Source:", actualVideoSrc);
      console.log("VideoPlayer - Force first frame:", forceFirstFrame);
      console.log("VideoPlayer - Is Mobile:", isMobile);
      console.log("VideoPlayer - Should use poster:", shouldUsePoster);
    } else {
      console.error("VideoPlayer - Missing video source");
    }
  }, [actualVideoSrc, forceFirstFrame, isMobile, shouldUsePoster]);

  useEffect(() => {
    if (onVisible) {
      onVisible();
    }
  }, [onVisible]);
  
  useEffect(() => {
    if (actualVideoSrc) {
      console.log("VideoPlayer - Source details:", actualVideoSrc);
      if (actualVideoSrc.includes('chunks=')) {
        const url = new URL(actualVideoSrc);
        const chunks = url.searchParams.get('chunks');
        const original = url.searchParams.get('original');
        const type = url.searchParams.get('type');
        console.log("VideoPlayer - Detected chunked video:", { chunks, original, type });
      }
    }
  }, [actualVideoSrc]);

  useEffect(() => {
    if (actualVideoSrc) {
      setKey(prevKey => prevKey + 1);
      setVideoError(null);
      setIsVideoLoaded(false);
      setFrameLoaded(false);
      console.log("VideoPlayer - Source changed, forcing re-render:", actualVideoSrc);
    }
  }, [actualVideoSrc]);

  // Special handling for first frame forcing, especially on mobile
  useEffect(() => {
    if (videoRef.current && forceFirstFrame && !frameLoaded) {
      const video = videoRef.current;
      
      console.log("Attempting to force first frame display");
      
      // Set up first frame loading handlers
      const handleMetadataLoaded = () => {
        console.log("Metadata loaded, seeking to first frame");
        
        // Ensure we're at the first frame (0 seconds)
        try {
          video.currentTime = 0;
          
          // For iOS we may need to load a bit more than just metadata
          if (isMobile) {
            // This helps on iOS to actually display the first frame
            video.play().then(() => {
              setTimeout(() => {
                video.pause();
                setFrameLoaded(true);
                console.log("First frame loaded successfully");
              }, 50);
            }).catch(err => {
              console.log("Autoplay attempt failed (expected on mobile):", err);
              // Still mark as loaded since we tried our best
              setFrameLoaded(true);
            });
          } else {
            setFrameLoaded(true);
          }
        } catch (err) {
          console.error("Error seeking to first frame:", err);
          setFrameLoaded(true); // Mark as done anyway
        }
      };
      
      video.addEventListener('loadedmetadata', handleMetadataLoaded);
      
      return () => {
        video.removeEventListener('loadedmetadata', handleMetadataLoaded);
      };
    }
  }, [forceFirstFrame, frameLoaded, isMobile]);

  // Sync muted state with video element
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  useEffect(() => {
    const videoElement = videoRef.current;
    
    if (videoElement && disableDownload) {
      const handleContextMenu = (e: MouseEvent) => {
        e.preventDefault();
        return false;
      };
      
      videoElement.addEventListener('contextmenu', handleContextMenu);
      
      return () => {
        videoElement.removeEventListener('contextmenu', handleContextMenu);
      };
    }
  }, [disableDownload]);

  useEffect(() => {
    if (controlledIsPlaying !== undefined) {
      setIsPlaying(controlledIsPlaying);
      
      if (videoRef.current) {
        if (controlledIsPlaying && videoRef.current.paused) {
          videoRef.current.play().catch(error => {
            console.error("Error playing video:", error);
            setIsPlaying(false);
            if (onPlayStateChange) onPlayStateChange(false);
          });
        } else if (!controlledIsPlaying && !videoRef.current.paused) {
          videoRef.current.pause();
        }
      }
    }
  }, [controlledIsPlaying, onPlayStateChange]);

  const getObjectFitClass = () => {
    if (!className) return "object-contain";
    
    if (className.includes('object-contain')) {
      return "object-contain";
    } else if (className.includes('object-cover')) {
      return "object-cover";
    }
    
    return "object-contain";
  };

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(error => {
          console.error("Error playing video:", error);
          
          if (!videoError) {
            toast.error("Unable to play this video. The file may be corrupted or in an unsupported format.");
          }
        });
      }
      
      const newPlayState = !isPlaying;
      setIsPlaying(newPlayState);
      if (onPlayStateChange) onPlayStateChange(newPlayState);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    if (onPlayStateChange) onPlayStateChange(false);
    if (onEnded) onEnded();
  };
  
  const handleVideoError = (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
    const videoElement = e.target as HTMLVideoElement;
    console.error("Video error:", e);
    console.error("Video error code:", videoElement.error?.code);
    console.error("Video error message:", videoElement.error?.message);
    console.error("Video URL with error:", actualVideoSrc);
    
    let errorMessage = "Error loading video";
    if (videoElement.error) {
      errorMessage += `: ${videoElement.error.message}`;
      
      if (actualVideoSrc && actualVideoSrc.includes('chunks=')) {
        errorMessage = "This video was uploaded in chunks and needs to be processed. Please contact the administrator.";
        toast.error("This video is still being processed. Please try again later or download it instead.");
      }
      else if (videoElement.error.code === 3 || videoElement.error.code === 4) {
        errorMessage = "The video format appears to be incompatible with your browser. Try downloading the video instead.";
        if (onOpenLicenseDialog) {
          toast.error("This video format may not play in your browser. Try downloading it instead.", {
            action: {
              label: "Download",
              onClick: onOpenLicenseDialog
            }
          });
        }
      }
    }
    
    setVideoError(errorMessage);
    
    if (retryCount < 2 && actualVideoSrc) {
      setRetryCount(retryCount + 1);
      console.log(`Retrying video load (attempt ${retryCount + 1}/2)...`);
      
      setTimeout(() => {
        setKey(prevKey => prevKey + 1);
        setVideoError(null);
        
        if (videoRef.current) {
          videoRef.current.load();
        }
      }, 1000);
    } else {
      // After retries, test if URL is accessible
      if (actualVideoSrc) {
        fetch(actualVideoSrc, { method: 'HEAD' })
          .then(response => {
            if (!response.ok) {
              console.error(`Video URL returned ${response.status} ${response.statusText}`);
              toast.error(`Video resource error: ${response.status} ${response.statusText}`);
            } else {
              console.log("Video URL is accessible, but browser cannot play the format");
              
              // Attempt to open the video directly as a special fallback
              if (!isMobile && onOpenLicenseDialog) {
                console.log("Suggesting direct video download as fallback");
                toast.error("Browser is having trouble playing this video format. Try downloading it instead.", {
                  action: {
                    label: "Download",
                    onClick: onOpenLicenseDialog
                  }
                });
              }
            }
          })
          .catch(fetchError => {
            console.error("Cannot access video URL:", fetchError);
            toast.error("Cannot access video: The URL may be incorrect or the resource is unavailable");
          });
      }
    }
    
    if (onError) {
      onError();
    }
  };

  const handleVideoLoaded = () => {
    console.log("Video loaded data successfully");
    setIsVideoLoaded(true);
    setVideoError(null);
    
    if (onLoad) onLoad();
  };

  const handleDownloadClick = () => {
    if (!isAuthenticated) {
      toast.error("Vous devez être connecté pour télécharger ce contenu.");
      navigate('/auth/login', { state: { returnUrl: window.location.pathname } });
      return;
    }
    
    if (onOpenLicenseDialog) {
      onOpenLicenseDialog();
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    // Stop event propagation to prevent triggering the play/pause toggle
    e.preventDefault();
    e.stopPropagation();
    
    if (videoRef.current) {
      const newMutedState = !isMuted;
      videoRef.current.muted = newMutedState;
      setIsMuted(newMutedState);
      console.log("Video mute toggled:", newMutedState);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === ' ' && document.activeElement === videoRef.current) {
        e.preventDefault();
        togglePlayPause();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPlaying]);

  useEffect(() => {
    const container = containerRef.current;
    
    const handleMouseEnter = () => setShowCustomControls(true);
    const handleMouseLeave = () => setShowCustomControls(false);
    
    if (container) {
      container.addEventListener('mouseenter', handleMouseEnter);
      container.addEventListener('mouseleave', handleMouseLeave);
    }
    
    return () => {
      if (container) {
        container.removeEventListener('mouseenter', handleMouseEnter);
        container.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  if (!actualVideoSrc) {
    return (
      <div className="flex items-center justify-center w-full h-full min-h-[200px] bg-muted/20 text-muted-foreground">
        <p>No video source provided</p>
      </div>
    );
  }

  // Determine if we should show the sound control button
  // Only show when explicitly requested or when video is playing
  const shouldShowSoundControls = showSoundControls || isPlaying;

  // Determine optimal preload strategy based on device and forceFirstFrame setting
  const effectivePreload = forceFirstFrame ? 
    "auto" : // Always use auto when forcing first frame
    (isMobile ? "metadata" : preload);

  return (
    <div 
      ref={containerRef}
      className={`relative overflow-hidden rounded-lg bg-black flex items-center justify-center ${className}`}
    >
      {videoError ? (
        <div className="flex flex-col items-center justify-center w-full h-full min-h-[200px] bg-muted/20 text-muted-foreground p-4">
          <p className="text-center mb-2">{videoError}</p>
          {onOpenLicenseDialog && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleDownloadClick}
              className="mt-2"
            >
              Download instead
            </Button>
          )}
        </div>
      ) : (
        <>
          <video
            key={key}
            ref={videoRef}
            className={`w-full h-full ${getObjectFitClass()}`}
            src={actualVideoSrc}
            poster={shouldUsePoster ? actualPosterSrc : undefined}
            autoPlay={autoplay}
            controls={controls && !hideControls}
            onPlay={() => {
              setIsPlaying(true);
              if (onPlayStateChange) onPlayStateChange(true);
            }}
            onPause={() => {
              setIsPlaying(false);
              if (onPlayStateChange) onPlayStateChange(false);
            }}
            onEnded={handleEnded}
            onError={handleVideoError}
            onLoadedData={handleVideoLoaded}
            onCanPlay={() => console.log("Video can play")}
            loop={loop}
            muted={isMuted}
            playsInline={playsInline}
            preload={effectivePreload}
            controlsList="nodownload"
            onCopy={(e) => disableDownload && e.preventDefault()}
            onCut={(e) => disableDownload && e.preventDefault()}
            crossOrigin="anonymous" // Add this to help with CORS issues
          />
          
          {/* Sound control button - only show when playing or explicitly requested */}
          {shouldShowSoundControls && (!controls || hideControls) && (
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMute}
              className="absolute bottom-3 right-3 bg-black/50 hover:bg-black/70 text-white backdrop-blur-sm rounded-full h-8 w-8 transition-all z-20"
            >
              {isMuted ? (
                <VolumeX className="h-4 w-4" />
              ) : (
                <Volume2 className="h-4 w-4" />
              )}
              <span className="sr-only">{isMuted ? 'Unmute' : 'Mute'}</span>
            </Button>
          )}
        </>
      )}
      
      {(!controls || hideControls) && !videoError && (
        <button
          className="absolute inset-0 w-full h-full flex items-center justify-center"
          onClick={togglePlayPause}
        >
          {!isPlaying && (
            <div className="bg-black/30 backdrop-blur-sm rounded-full p-5 w-16 h-16 flex items-center justify-center transition-transform duration-200 hover:scale-110">
              <Play className="h-8 w-8 text-white" />
            </div>
          )}
        </button>
      )}
    </div>
  );
};
